/**
 * Sidebar for Team Chat showing channels, DMs, and groups.
 */

import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  Typography,
  IconButton,
  Badge,
  Input,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/joy';
import {
  Tag as TagIcon,
  Lock as LockIcon,
  User as PersonIcon,
  Users as GroupIcon,
  Briefcase as WorkIcon,
  MagnifyingGlass as SearchIcon,
  Plus as AddIcon,
  CaretDown as ExpandMoreIcon,
  X as CloseIcon,
  Gear as SettingsIcon,
} from '@phosphor-icons/react';
import { useChannels, useDirectMessages, useChatGroups, useUnreadCounts, useHideChannel } from '../../hooks/useChat';
import { useAuth } from '../../contexts/AuthContext';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';
import type { Channel, DirectMessageConversation, ChatGroup } from '../../types/chat';
import CreateChannelModal from './CreateChannelModal';
import NewDMModal from './NewDMModal';
import CreateGroupModal from './CreateGroupModal';
import EditGroupModal from './EditGroupModal';

interface ChatSidebarProps {
  activeChannelId?: string | null;
  onChannelSelect: (channelId: string) => void;
  onDMSelect: (userId: string) => void;
  onClose?: () => void;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  activeChannelId,
  onChannelSelect,
  onDMSelect,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'channels',
    'dms',
    'groups',
  ]);

  // Modal state
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [newDMOpen, setNewDMOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [editGroupId, setEditGroupId] = useState<string | null>(null);

  const { user } = useAuth();
  const { data: channelsData, isLoading: channelsLoading } = useChannels();
  const { data: dmsData, isLoading: dmsLoading } = useDirectMessages();
  const { data: groupsData, isLoading: groupsLoading } = useChatGroups();
  const { data: unreadCountsData } = useUnreadCounts();
  const hideChannel = useHideChannel();

  // Ensure we always have arrays (handle API errors/unexpected responses)
  const channels = Array.isArray(channelsData) ? channelsData : [];
  const dms = Array.isArray(dmsData) ? dmsData : [];
  const groups = Array.isArray(groupsData) ? groupsData : [];
  const unreadCounts = Array.isArray(unreadCountsData) ? unreadCountsData : [];

  // Separate channels by type
  const orgChannels = channels.filter((c) => c.channel_type === 'organization');
  const jobChannels = channels.filter((c) => c.channel_type === 'job');

  // Get unread count for a channel
  const getUnreadCount = (channelId: string): number => {
    const unread = unreadCounts.find((u) => u.channel_id === channelId);
    return unread?.unread_count || 0;
  };

  // Filter items by search
  const filterItems = <T extends { name?: string }>(
    items: T[],
    query: string
  ): T[] => {
    if (!query.trim()) return items;
    const lowerQuery = query.toLowerCase();
    return items.filter((item) =>
      item.name?.toLowerCase().includes(lowerQuery)
    );
  };

  const filteredOrgChannels = filterItems(orgChannels, searchQuery);
  const filteredJobChannels = filterItems(jobChannels, searchQuery);
  const filteredGroups = filterItems(groups, searchQuery);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section]
    );
  };

  const renderChannelItem = (channel: Channel) => {
    const unread = getUnreadCount(channel.id);
    const isActive = activeChannelId === channel.id;

    return (
      <ListItem key={channel.id}>
        <ListItemButton
          selected={isActive}
          onClick={() => onChannelSelect(channel.id)}
          sx={{
            borderRadius: 'sm',
            fontWeight: unread > 0 ? 'bold' : 'normal',
          }}
        >
          <ListItemDecorator>
            {channel.is_private ? (
              <LockIcon size={18} />
            ) : channel.channel_type === 'job' ? (
              <WorkIcon size={18} />
            ) : (
              <TagIcon size={18} />
            )}
          </ListItemDecorator>
          <ListItemContent>
            <Typography
              level="body-sm"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: unread > 0 ? 'bold' : 'normal',
              }}
            >
              {channel.name}
            </Typography>
          </ListItemContent>
          {unread > 0 && (
            <Badge
              badgeContent={unread}
              color="primary"
              size="sm"
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const renderDMItem = (dm: DirectMessageConversation) => {
    const isActive = activeChannelId === dm.channel.id;

    return (
      <ListItem
        key={dm.channel.id}
        endAction={
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={(e) => {
              e.stopPropagation();
              hideChannel.mutate(dm.channel.id);
            }}
            sx={{
              opacity: 0,
              transition: 'opacity 0.15s',
              '.MuiListItem-root:hover &': { opacity: 1 },
            }}
          >
            <CloseIcon size={18} />
          </IconButton>
        }
      >
        <ListItemButton
          selected={isActive}
          onClick={() => onChannelSelect(dm.channel.id)}
          sx={{
            borderRadius: 'sm',
            fontWeight: dm.unread_count > 0 ? 'bold' : 'normal',
          }}
        >
          <ListItemDecorator>
            <PersonIcon size={18} />
          </ListItemDecorator>
          <ListItemContent>
            <Typography
              level="body-sm"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: dm.unread_count > 0 ? 'bold' : 'normal',
              }}
            >
              {dm.other_user.full_name || dm.other_user.username}
            </Typography>
          </ListItemContent>
          {dm.unread_count > 0 && (
            <Badge
              badgeContent={dm.unread_count}
              color="primary"
              size="sm"
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const renderGroupItem = (group: ChatGroup) => {
    const unread = group.channel ? getUnreadCount(group.channel.id) : 0;
    const isActive = group.channel ? activeChannelId === group.channel.id : false;

    return (
      <ListItem
        key={group.id}
        endAction={
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={(e) => {
                e.stopPropagation();
                setEditGroupId(group.id);
              }}
              sx={{
                opacity: 0,
                transition: 'opacity 0.15s',
                '.MuiListItem-root:hover &': { opacity: 1 },
              }}
            >
              <SettingsIcon size={18} />
            </IconButton>
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={(e) => {
                e.stopPropagation();
                if (group.channel) {
                  hideChannel.mutate(group.channel.id);
                }
              }}
              sx={{
                opacity: 0,
                transition: 'opacity 0.15s',
                '.MuiListItem-root:hover &': { opacity: 1 },
              }}
            >
              <CloseIcon size={18} />
            </IconButton>
          </Box>
        }
      >
        <ListItemButton
          selected={isActive}
          onClick={() => group.channel && onChannelSelect(group.channel.id)}
          sx={{
            borderRadius: 'sm',
            fontWeight: unread > 0 ? 'bold' : 'normal',
          }}
        >
          <ListItemDecorator>
            <GroupIcon size={18} />
          </ListItemDecorator>
          <ListItemContent>
            <Typography
              level="body-sm"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: unread > 0 ? 'bold' : 'normal',
              }}
            >
              {group.name}
            </Typography>
          </ListItemContent>
          {unread > 0 && (
            <Badge
              badgeContent={unread}
              color="primary"
              size="sm"
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.surface',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography level="title-md">Team Chat</Typography>
          <PageHelpButton />
        </Box>
        {onClose && (
          <IconButton
            variant="plain"
            color="neutral"
            size="sm"
            onClick={onClose}
            sx={{ display: { xs: 'flex', md: 'flex' } }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Search */}
      <Box sx={{ px: 2, py: 1 }}>
        <Input
          placeholder="Search..."
          size="sm"
          startDecorator={<SearchIcon />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: '100%' }}
        />
      </Box>

      {/* Channel Lists */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
        {/* Organization Channels */}
        <Accordion
          expanded={expandedSections.includes('channels')}
          onChange={() => toggleSection('channels')}
        >
          <AccordionSummary
            indicator={<ExpandMoreIcon />}
            sx={{ minHeight: 40 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                pr: 1,
              }}
            >
              <Typography level="body-sm" fontWeight="lg">
                Channels
              </Typography>
              <Box
                component="span"
                role="button"
                tabIndex={0}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setCreateChannelOpen(true);
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    setCreateChannelOpen(true);
                  }
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.5,
                  borderRadius: 'sm',
                  cursor: 'pointer',
                  color: 'neutral.500',
                  '&:hover': {
                    bgcolor: 'neutral.100',
                    color: 'neutral.700',
                  },
                }}
              >
                <AddIcon size={18} />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List size="sm" sx={{ '--ListItem-paddingY': '2px' }}>
              {filteredOrgChannels.map(renderChannelItem)}
              {filteredOrgChannels.length === 0 && !channelsLoading && (
                <ListItem>
                  <Typography level="body-xs" color="neutral">
                    No channels found
                  </Typography>
                </ListItem>
              )}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Job Channels */}
        {filteredJobChannels.length > 0 && (
          <Accordion
            expanded={expandedSections.includes('jobs')}
            onChange={() => toggleSection('jobs')}
          >
            <AccordionSummary
              indicator={<ExpandMoreIcon />}
              sx={{ minHeight: 40 }}
            >
              <Typography level="body-sm" fontWeight="lg">
                Job Channels
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List size="sm" sx={{ '--ListItem-paddingY': '2px' }}>
                {filteredJobChannels.map(renderChannelItem)}
              </List>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Direct Messages */}
        <Accordion
          expanded={expandedSections.includes('dms')}
          onChange={() => toggleSection('dms')}
        >
          <AccordionSummary
            indicator={<ExpandMoreIcon />}
            sx={{ minHeight: 40 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                pr: 1,
              }}
            >
              <Typography level="body-sm" fontWeight="lg">
                Direct Messages
              </Typography>
              <Box
                component="span"
                role="button"
                tabIndex={0}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setNewDMOpen(true);
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    setNewDMOpen(true);
                  }
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.5,
                  borderRadius: 'sm',
                  cursor: 'pointer',
                  color: 'neutral.500',
                  '&:hover': {
                    bgcolor: 'neutral.100',
                    color: 'neutral.700',
                  },
                }}
              >
                <AddIcon size={18} />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List size="sm" sx={{ '--ListItem-paddingY': '2px' }}>
              {dms.map(renderDMItem)}
              {dms.length === 0 && !dmsLoading && (
                <ListItem>
                  <Typography level="body-xs" color="neutral">
                    No conversations yet
                  </Typography>
                </ListItem>
              )}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Groups */}
        <Accordion
          expanded={expandedSections.includes('groups')}
          onChange={() => toggleSection('groups')}
        >
          <AccordionSummary
            indicator={<ExpandMoreIcon />}
            sx={{ minHeight: 40 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                pr: 1,
              }}
            >
              <Typography level="body-sm" fontWeight="lg">
                Groups
              </Typography>
              <Box
                component="span"
                role="button"
                tabIndex={0}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setCreateGroupOpen(true);
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    setCreateGroupOpen(true);
                  }
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0.5,
                  borderRadius: 'sm',
                  cursor: 'pointer',
                  color: 'neutral.500',
                  '&:hover': {
                    bgcolor: 'neutral.100',
                    color: 'neutral.700',
                  },
                }}
              >
                <AddIcon size={18} />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List size="sm" sx={{ '--ListItem-paddingY': '2px' }}>
              {filteredGroups.map(renderGroupItem)}
              {filteredGroups.length === 0 && !groupsLoading && (
                <ListItem>
                  <Typography level="body-xs" color="neutral">
                    No groups yet
                  </Typography>
                </ListItem>
              )}
            </List>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Modals */}
      <CreateChannelModal
        open={createChannelOpen}
        onClose={() => setCreateChannelOpen(false)}
        onSuccess={(channelId) => onChannelSelect(channelId)}
      />

      <NewDMModal
        open={newDMOpen}
        onClose={() => setNewDMOpen(false)}
        onSuccess={(channelId) => onChannelSelect(channelId)}
        currentUserId={user?.id}
      />

      <CreateGroupModal
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
        onSuccess={(channelId) => onChannelSelect(channelId)}
        currentUserId={user?.id}
      />

      {editGroupId && (
        <EditGroupModal
          open={!!editGroupId}
          onClose={() => setEditGroupId(null)}
          groupId={editGroupId}
          currentUserId={user?.id}
        />
      )}
    </Box>
  );
};

export default ChatSidebar;
